import type { FC, SVGProps } from "react";
import styles from "./Icon.module.css";

/**
 * Icon — thin wrapper around @untitledui/icons (and any icon component
 * that exports an `FC<SVGProps<SVGSVGElement>>`).
 *
 * Centralises size + color decisions so components don't have to repeat
 * `<XClose size={16} color="var(--icon-default)" />` at every call site.
 * Pass any icon React component as `as`; the wrapper applies size/tone via
 * CSS classes on a surrounding <span>, and the svg inherits via
 * `currentColor` and `width/height: 1em` (which picks up font-size).
 *
 * The type is deliberately library-agnostic — any icon package that ships
 * React components satisfying `FC<SVGProps<SVGSVGElement>>` works without
 * changes to the wrapper. This was originally wired to react-icons, then
 * swapped to @untitledui/icons; future swaps don't require type churn.
 *
 * Accessibility: icons used purely for decoration should omit `label` so the
 * wrapper renders `aria-hidden="true"`. Icons that convey information should
 * pass `label` which becomes the `aria-label` on the wrapper.
 */

type Size = "xs" | "sm" | "md" | "lg";
type Tone = "default" | "muted" | "negative" | "on-dark" | "outline" | "inherit";

/**
 * Accepts any React component that renders an <svg> and takes standard SVG
 * props. Both @untitledui/icons (FC<Props extends SVGProps<SVGSVGElement>>)
 * and react-icons (IconType extends FC<IconBaseProps>) satisfy this shape.
 */
export type IconComponent = FC<SVGProps<SVGSVGElement>>;

export type IconProps = {
  /** The icon component to render (e.g. `XClose` from @untitledui/icons). */
  as: IconComponent;
  /** Icon size. xs=14px, sm=16px, md=20px, lg=24px. */
  size?: Size;
  /** Color token. `inherit` uses the parent's `currentColor`. */
  tone?: Tone;
  /** a11y label. Omit for decorative icons (renders `aria-hidden="true"`). */
  label?: string;
  className?: string;
};

export function Icon({
  // Rename the local alias to avoid shadowing the exported `IconComponent`
  // type (also defined in this file's scope). `Svg` reads clearly inside
  // the JSX below — the thing being rendered is, in fact, an <svg>.
  as: Svg,
  size = "md",
  tone = "default",
  label,
  className,
}: IconProps) {
  const classes = [styles.icon, styles[`size-${size}`], styles[`tone-${tone}`], className]
    .filter(Boolean)
    .join(" ");
  const a11y = label ? { "aria-label": label, role: "img" } : { "aria-hidden": true };
  return (
    <span className={classes} {...a11y}>
      {/* DO NOT pass `size` or `color` props to <Svg />. Sizing is
       * controlled via `font-size` on the wrapper span + `width/height: 1em`
       * on the child svg (see Icon.module.css). Color is controlled via
       * `color: inherit` on the svg and the icon library's own
       * `stroke="currentColor"` default. Passing size={…} here would
       * set explicit width/height attributes that compete with the CSS
       * rule and silently break the sm/md/lg presets. */}
      <Svg />
    </span>
  );
}
